/*
  Loading Screen controller.

  Zero animation libraries. The looping animation and the one-shot exit are both
  pure, compositor-only CSS (see loading-screen.css):
  - The loop runs as CSS @keyframes (transform/opacity), so it stays perfectly
    smooth even while the main thread is saturated parsing/executing the page's
    JS during load.
  - The exit is a single `.ls--exit` class toggle that triggers a vibe-matched
    CSS animation per variant. No GSAP is loaded for the loading screen, so the
    ~72KB library no longer competes for the network/main thread at first paint.

  Robustness:
  - prefers-reduced-motion: CSS shows a calm static state; we dismiss instantly.
  - Inline failsafe in the snippet guarantees the overlay is removed even if this
    file never runs, so the user is never trapped.

  Config is read from data-* attributes on #UntamedLoadingScreen.
*/
(function () {
  var root = document.getElementById('UntamedLoadingScreen');
  if (!root || root.dataset.lsInit === 'true') return;
  root.dataset.lsInit = 'true';

  var d = root.dataset;
  var mode = d.mode === 'timer' ? 'timer' : 'page_load';
  var seconds = parseFloat(d.seconds);
  if (!isFinite(seconds) || seconds <= 0) seconds = 3;
  var maxMs = parseInt(d.maxMs, 10);
  if (!isFinite(maxMs) || maxMs <= 0) maxMs = 10000;
  var minShowMs = 600;

  var startedAt = Date.now();
  var dismissed = false;
  var timers = [];

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clearTimers() {
    for (var i = 0; i < timers.length; i++) clearTimeout(timers[i]);
    timers = [];
  }

  function unlockScroll() {
    var html = document.documentElement;
    html.style.overflow = html.dataset.lsPrevOverflow || '';
  }

  function removeOverlay() {
    unlockScroll();
    if (root && root.parentNode) root.parentNode.removeChild(root);
  }

  // Lock scroll while the overlay is up.
  (function lockScroll() {
    var html = document.documentElement;
    html.dataset.lsPrevOverflow = html.style.overflow || '';
    html.style.overflow = 'hidden';
  })();

  function instantHide() {
    root.classList.add('ls--instant-hide');
    timers.push(setTimeout(removeOverlay, 350));
  }

  // CSS-driven exit: toggle `.ls--exit`, remove on animationend (with a timeout
  // authority for techwear, whose root element itself doesn't animate, and as a
  // guard against a missed event).
  function playExit() {
    root.classList.add('ls--exit');
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      root.removeEventListener('animationend', onEnd);
      removeOverlay();
    }
    function onEnd(e) {
      if (e.target === root) finish();
    }
    root.addEventListener('animationend', onEnd);
    timers.push(setTimeout(finish, 1100));
  }

  function dismiss(animated) {
    if (dismissed) return;
    dismissed = true;
    clearTimers();
    var elapsed = Date.now() - startedAt;
    var wait = Math.max(0, minShowMs - elapsed);
    setTimeout(function () {
      if (animated && !reduceMotion) playExit();
      else instantHide();
    }, wait);
  }

  // Failsafe: never let the overlay stick around.
  timers.push(setTimeout(function () { dismiss(true); }, maxMs));

  // ---- Schedule dismissal per mode ----------------------------------------
  if (mode === 'timer') {
    timers.push(setTimeout(function () { dismiss(true); }, seconds * 1000));
  } else {
    var onReady = function () {
      var fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
      var fontCap = new Promise(function (resolve) { timers.push(setTimeout(resolve, 1500)); });
      Promise.race([fontsReady, fontCap]).then(function () { dismiss(true); });
    };
    if (document.readyState === 'complete') onReady();
    else window.addEventListener('load', onReady, { once: true });
  }

  // The looping animation is already running as compositor-only CSS @keyframes
  // in the markup (loading-screen.css). Under reduced motion CSS shows a calm
  // static state. Nothing further to start here.
})();
